import { createAppSlice } from "@/app/createAppSlice"
import type { PostDetailResponse, CommentResponse } from "@/types/domain"
import {
  getPostDetail,
  getComments,
  createComment,
  togglePostLike,
  toggleCommentLike,
} from "@/services/api"

interface PostDetailState {
  post: PostDetailResponse | null
  comments: CommentResponse[]
  status: "idle" | "loading" | "succeeded" | "failed"
  error: string | null
  commentStatus: "idle" | "loading" | "succeeded" | "failed" // 별도 로딩 상태
  commentPage: number
  hasMoreComments: boolean
}

const initialState: PostDetailState = {
  post: null,
  comments: [],
  status: "idle",
  error: null,
  commentStatus: "idle",
  commentPage: 0,
  hasMoreComments: true,
}

export const postDetailSlice = createAppSlice({
  name: "postDetail",
  initialState,
  reducers: create => ({
    resetPostDetail: create.reducer(state => {
      state.post = null
      state.comments = []
      state.status = "idle"
      state.commentStatus = "idle"
      state.commentPage = 0
      state.hasMoreComments = true
    }),
    fetchPost: create.asyncThunk(
      async (postId: string) => {
        const response = await getPostDetail(postId)
        return response
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "succeeded"
          state.post = action.payload
        },
        rejected: state => {
          state.status = "failed"
        },
      },
    ),
    fetchComments: create.asyncThunk(
      async ({ postId, page }: { postId: string; page: number }) => {
        const response = await getComments(postId, page)
        return response
      },
      {
        pending: state => {
          state.commentStatus = "loading"
        },
        fulfilled: (state, action) => {
          state.commentStatus = "succeeded"
          if (action.payload.number === 0) {
            state.comments = action.payload.content
          } else {
            state.comments = [...state.comments, ...action.payload.content]
          }
          state.hasMoreComments = !action.payload.last
          state.commentPage = action.payload.number
        },
      },
    ),
    addComment: create.asyncThunk(
      async (
        {
          postId,
          content,
          parentCommentId,
        }: { postId: string; content: string; parentCommentId?: string },
        { dispatch },
      ) => {
        await createComment(postId, { content, parentCommentId })
        // 댓글 작성 후 목록 갱신 (간단한 구현을 위해 다시 첫 페이지부터 로드하거나, 추가된 댓글만 별도 처리 가능)
        // 여기서는 다시 로드하는 방식을 택함 (최신 댓글 확인)
        dispatch(postDetailSlice.actions.fetchComments({ postId, page: 0 }))
      },
      {
        pending: state => {
          state.commentStatus = "loading"
        },
      },
    ),
    toggleLikePost: create.asyncThunk(
      async (postId: string) => {
        const response = await togglePostLike(postId)
        return response
      },
      {
        pending: state => {
          // 낙관적 업데이트
          if (state.post) {
            const wasLiked = state.post.liked
            state.post.liked = !wasLiked
            state.post.likeCount += wasLiked ? -1 : 1
          }
        },
        fulfilled: (state, action) => {
          // 서버 응답으로 동기화 (실패 시 롤백 로직이 필요할 수 있음)
          if (state.post) {
            state.post.liked = action.payload.liked
            state.post.likeCount = action.payload.likeCount
          }
        },
        rejected: (state) => {
           // 롤백
           if (state.post) {
             const isLiked = state.post.liked
             state.post.liked = !isLiked
             state.post.likeCount += isLiked ? -1 : 1
           }
        }
      },
    ),
    toggleLikeComment: create.asyncThunk(
        async (commentId: string) => {
            const response = await toggleCommentLike(commentId)
            return { commentId, ...response }
        },
        {
            pending: (state, action) => {
               // 낙관적 업데이트를 위해 commentId를 알아야 하는데 asyncThunk의 pending action meta에서 가져올 수 있음
               const commentId = action.meta.arg
               const findAndToggle = (comments: CommentResponse[]) => {
                   for (const comment of comments) {
                       if (comment.id === commentId) {
                           const wasLiked = comment.liked
                           comment.liked = !wasLiked
                           comment.likeCount += wasLiked ? -1 : 1
                           return true
                       }
                       if (comment.children && comment.children.length > 0) {
                           if (findAndToggle(comment.children)) return true
                       }
                   }
                   return false
               }
               findAndToggle(state.comments)
            },
            fulfilled: (state, action) => {
                const { commentId, liked, likeCount } = action.payload
                const findAndUpdate = (comments: CommentResponse[]) => {
                    for (const comment of comments) {
                        if (comment.id === commentId) {
                            comment.liked = liked
                            comment.likeCount = likeCount
                            return true
                        }
                        if (comment.children && comment.children.length > 0) {
                            if (findAndUpdate(comment.children)) return true
                        }
                    }
                    return false
                }
                findAndUpdate(state.comments)
            },
            rejected: (state, action) => {
                 // 롤백
                 const commentId = action.meta.arg
                 const findAndToggle = (comments: CommentResponse[]) => {
                    for (const comment of comments) {
                        if (comment.id === commentId) {
                            const isLiked = comment.liked
                            comment.liked = !isLiked
                            comment.likeCount += isLiked ? -1 : 1
                            return true
                        }
                         if (comment.children && comment.children.length > 0) {
                            if (findAndToggle(comment.children)) return true
                        }
                    }
                    return false
                }
                findAndToggle(state.comments)
            }
        }
    )
  }),
  selectors: {
    selectPost: state => state.post,
    selectComments: state => state.comments,
    selectPostStatus: state => state.status,
    selectCommentStatus: state => state.commentStatus,
  },
})

export const {
  resetPostDetail,
  fetchPost,
  fetchComments,
  addComment,
  toggleLikePost,
  toggleLikeComment
} = postDetailSlice.actions

export const { selectPost, selectComments, selectPostStatus, selectCommentStatus } =
  postDetailSlice.selectors
